/**
 * Room lifecycle: the pure state machine, with no Firestore in sight.
 *
 * Rooms are the first thing in this app with a TTL — tribes and forums have no
 * expiry concept at all — so there was no existing transition logic to lean on.
 * Keeping it dependency-free is what makes it testable: every rule below is
 * exercised by roomLifecycle.test.ts without an emulator, a network, or a mock.
 *
 * RoomService performs the writes; this module decides what those writes are.
 * The scheduled sweep in functions/rooms.js re-implements only the two
 * time-based checks (grace timeout, expiry) against the same constants, because
 * a Cloud Function cannot import from src/. Those constants are duplicated
 * there and cross-referenced in both directions — change them together.
 */

export const ROOM_MIN_MEMBERS = 3;
export const ROOM_MAX_MEMBERS = 15;
export const ROOM_MAX_MODS = 3;

/**
 * How a room decides who gets in.
 *
 *   open      anyone in the age bracket walks in, no approval step
 *   approval  the host or a room mod approves each request (the default, and
 *             what every room did before this existed)
 *   question  the host sets a question; a request must answer it, and the
 *             answer is shown to whoever approves
 *
 * `question` is deliberately NOT auto-matched against a stored correct answer.
 * A right answer would have to live on the room document to be compared, and
 * that document is readable by every signed-in user in the bracket — the answer
 * would leak to exactly the people it is meant to screen. The answer qualifies
 * the request in the approver's judgement instead.
 */
export type RoomJoinPolicy = 'open' | 'approval' | 'question';

export const ROOM_JOIN_POLICIES: RoomJoinPolicy[] = ['open', 'approval', 'question'];
export const ROOM_QUESTION_MAX_LENGTH = 140;
export const ROOM_ANSWER_MAX_LENGTH = 300;

/** Absent or unrecognised reads as 'approval' — the safe default, not the open one. */
export function normalizeJoinPolicy(value: any): RoomJoinPolicy {
  return ROOM_JOIN_POLICIES.includes(value) ? value : 'approval';
}

export const ROOM_MIN_DURATION_MINUTES = 60;    // 1h
export const ROOM_MAX_DURATION_MINUTES = 2880;  // 48h (2 days)

/**
 * How long a room may sit below the minimum before it auto-closes.
 *
 * The countdown PAUSES for this window rather than running through it (see
 * transitionForMemberCount). Two hours is a real chance for someone to come
 * back; it is capped so a room cannot sit paused indefinitely and outlive the
 * evening it was made for.
 */
export const ROOM_GRACE_PERIOD_MINUTES = 120;

/** Members are warned once the room has this long or less remaining. */
export const ROOM_ENDING_SOON_MINUTES = 30;

export const ROOM_MUTE_MIN_MINUTES = 5;
export const ROOM_MUTE_MAX_MINUTES = 60;

export const MINUTE_MS = 60_000;

export type RoomStatus = 'lobby' | 'active' | 'grace' | 'closed';
export type RoomMemberRole = 'owner' | 'mod' | 'member';
export type RoomMemberStatus = 'active' | 'left' | 'kicked';

/** The subset of a room document this module reasons about, in milliseconds. */
export interface RoomLifecycleState {
  status: RoomStatus;
  memberCount: number;
  durationMinutes: number;
  extended: boolean;
  startedAt: number | null;
  expiresAt: number | null;
  gracePeriodStartedAt: number | null;
}

/** A partial room-document patch. An empty object means "nothing changes". */
export interface RoomPatch {
  status?: RoomStatus;
  startedAt?: number | null;
  expiresAt?: number | null;
  gracePeriodStartedAt?: number | null;
}

export function isTerminal(status: RoomStatus): boolean {
  return status === 'closed';
}

/** A room accepts messages and counts down only while active or in grace. */
export function isLive(status: RoomStatus): boolean {
  return status === 'active' || status === 'grace';
}

/**
 * What the room document becomes when its active membership lands on
 * `memberCount`. Call this after every join, leave, and kick.
 *
 * The four transitions that matter:
 *   lobby  + enough members  → active, and the countdown starts here (never in lobby)
 *   active + too few members → grace, stamping the 30-minute clock
 *   grace  + enough members  → active, clearing the clock so it can start over later
 *   grace  + still too few   → nothing; the clock keeps running (see graceHasExpired)
 */
export function transitionForMemberCount(
  state: RoomLifecycleState,
  memberCount: number,
  nowMs: number,
): RoomPatch {
  if (isTerminal(state.status)) return {};

  const enough = memberCount >= ROOM_MIN_MEMBERS;

  if (state.status === 'lobby') {
    if (!enough) return {};
    return {
      status: 'active',
      startedAt: nowMs,
      expiresAt: nowMs + state.durationMinutes * MINUTE_MS,
    };
  }

  if (state.status === 'active' && !enough) {
    return { status: 'grace', gracePeriodStartedAt: nowMs };
  }

  if (state.status === 'grace' && enough) {
    // Recovered in time, and the clock RESUMES where it paused.
    //
    // It used to keep its original expiresAt, so a room that emptied for an
    // hour silently burned an hour of the time it had been given — the people
    // who came back got a shorter room than the one they made, for something
    // that was not their doing. Pushing expiry forward by exactly the paused
    // span gives back the time and no more.
    const pausedFor = state.gracePeriodStartedAt == null
      ? 0
      : Math.max(0, nowMs - state.gracePeriodStartedAt);

    // Clamped to 48h from the moment the room went live, because firestore.rules
    // enforces exactly that ceiling on any expiresAt change — an unclamped
    // resume would be REJECTED and the recovery transaction would fail outright,
    // which is worse than giving back slightly less time.
    //
    // It only ever bites a room already at the maximum duration; a 2h room that
    // pauses for 2h is nowhere near the ceiling and gets every minute back.
    const ceiling = state.startedAt == null
      ? null
      : state.startedAt + ROOM_MAX_DURATION_MINUTES * MINUTE_MS;

    const resumed = state.expiresAt != null && pausedFor > 0
      ? (ceiling == null ? state.expiresAt + pausedFor : Math.min(state.expiresAt + pausedFor, ceiling))
      : null;

    return {
      status: 'active',
      gracePeriodStartedAt: null,
      ...(resumed != null && resumed > (state.expiresAt as number)
        ? { expiresAt: resumed }
        : {}),
    };
  }

  return {};
}

/** True once a room has sat in grace for the full window without recovering. */
export function graceHasExpired(state: RoomLifecycleState, nowMs: number): boolean {
  if (state.status !== 'grace' || state.gracePeriodStartedAt == null) return false;
  return nowMs - state.gracePeriodStartedAt >= ROOM_GRACE_PERIOD_MINUTES * MINUTE_MS;
}

/**
 * True once the countdown has run out.
 *
 * A lobby room has no expiresAt and so never expires on a timer: the clock only
 * starts when the room goes active. An abandoned lobby therefore lives until its
 * host ends it manually.
 */
export function hasExpired(state: RoomLifecycleState, nowMs: number): boolean {
  if (isTerminal(state.status) || state.expiresAt == null) return false;
  // A room in grace has a PAUSED clock, so it cannot expire on the timer —
  // graceHasExpired is what ends it, after the grace window runs out. Without
  // this the pause would be decorative: the room would still die at its
  // original expiry while nominally frozen.
  if (state.status === 'grace') return false;
  return nowMs >= state.expiresAt;
}

/** True while the room is inside its final window and still live. */
export function isEndingSoon(state: RoomLifecycleState, nowMs: number): boolean {
  if (!isLive(state.status) || state.expiresAt == null) return false;
  const remaining = state.expiresAt - nowMs;
  return remaining > 0 && remaining <= ROOM_ENDING_SOON_MINUTES * MINUTE_MS;
}

export function canExtend(state: RoomLifecycleState): boolean {
  return isLive(state.status) && !state.extended;
}

/**
 * The new expiry after a one-time extension, clamped so a room can never run
 * more than 24h from the moment it went live. Returns null when the room may not
 * be extended at all, so callers surface one refusal rather than two.
 */
export function extendedExpiresAt(
  state: RoomLifecycleState,
  additionalMinutes: number,
  nowMs: number,
): number | null {
  if (!canExtend(state)) return null;
  if (!Number.isFinite(additionalMinutes) || additionalMinutes <= 0) return null;

  const base = state.expiresAt ?? nowMs;
  const ceiling = (state.startedAt ?? nowMs) + ROOM_MAX_DURATION_MINUTES * MINUTE_MS;
  const proposed = base + additionalMinutes * MINUTE_MS;

  // Already at the ceiling: refuse rather than burn the one-time extension on
  // zero extra minutes.
  if (base >= ceiling) return null;

  return Math.min(proposed, ceiling);
}

export function isValidDurationMinutes(minutes: number): boolean {
  return Number.isInteger(minutes)
    && minutes >= ROOM_MIN_DURATION_MINUTES
    && minutes <= ROOM_MAX_DURATION_MINUTES;
}

export function isValidMuteMinutes(minutes: number): boolean {
  return Number.isInteger(minutes)
    && minutes >= ROOM_MUTE_MIN_MINUTES
    && minutes <= ROOM_MUTE_MAX_MINUTES;
}

export function isFull(memberCount: number): boolean {
  return memberCount >= ROOM_MAX_MEMBERS;
}

/**
 * Whether a room can take one more member right now.
 *
 * Deliberately NOT a check on pending requests: a request may sit in the queue
 * while the room is full, and is only refused at the moment someone tries to
 * approve it. Nothing auto-denies, and nothing auto-approves when a slot frees.
 */
export function canAcceptMember(state: Pick<RoomLifecycleState, 'status' | 'memberCount'>): boolean {
  return !isTerminal(state.status) && !isFull(state.memberCount);
}

/**
 * Whether this user may open a new join request for this room.
 *
 * A kick is permanent for that room instance, so the kicked membership document
 * is left in place precisely so this check has something to find. Someone who
 * left voluntarily gets to ask again.
 */
export function canRequestToJoin(
  membership: { status?: RoomMemberStatus } | null | undefined,
): boolean {
  if (!membership) return true;
  if (membership.status === 'kicked') return false;
  return membership.status !== 'active';
}

export function isMuted(
  membership: { mutedUntil?: number | null } | null | undefined,
  nowMs: number,
): boolean {
  if (!membership?.mutedUntil) return false;
  return membership.mutedUntil > nowMs;
}

/** Formats remaining time for the in-room header: "3h 12m left", "48s left". */
export function formatTimeRemaining(expiresAtMs: number | null, nowMs: number): string {
  if (expiresAtMs == null) return 'Waiting to start';
  const ms = expiresAtMs - nowMs;
  if (ms <= 0) return 'Ending';

  const totalMinutes = Math.floor(ms / MINUTE_MS);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Past a day, count in days and hours. "47h 12m left" is technically correct
  // and useless — nobody reads a two-day room's clock in minutes.
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const restHours = hours % 24;
    return restHours > 0
      ? `${days}d ${restHours}h left`
      : `${days}d left`;
  }

  if (hours > 0) return `${hours}h ${minutes}m left`;
  if (totalMinutes > 0) return `${totalMinutes}m left`;
  return `${Math.max(1, Math.floor(ms / 1000))}s left`;
}
